import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterHeader from './components/FilterHeader';
import PatientMetricsCard from './components/PatientMetricsCard';
import PatientJourneyFunnel from './components/PatientJourneyFunnel';
import DemographicsPanel from './components/DemographicsPanel';
import OutcomeHeatmap from './components/OutcomeHeatmap';

const PatientCareAnalytics = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filters, setFilters] = useState({
    cohort: 'all',
    treatment: 'all',
    period: '30d',
    comparison: 'mom'
  });

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // Here you would typically trigger data refresh based on new filters
    console.log('Filters updated:', newFilters);
  };

  const patientMetrics = [
    {
      title: 'Patient Satisfaction',
      value: '94.2',
      unit: '%',
      change: '+2.3%',
      changeType: 'positive',
      threshold: 90,
      icon: 'Heart',
      color: 'var(--color-success)'
    },
    {
      title: 'Readmission Rate',
      value: '8.7',
      unit: '%',
      change: '-1.2%',
      changeType: 'positive',
      threshold: 10,
      icon: 'RotateCcw',
      color: 'var(--color-warning)'
    },
    {
      title: 'Treatment Success',
      value: '91.8',
      unit: '%',
      change: '+0.8%',
      changeType: 'positive',
      threshold: 85,
      icon: 'CheckCircle',
      color: 'var(--color-novus-green)'
    },
    {
      title: 'Avg Length of Stay',
      value: '4.2',
      unit: 'days',
      change: '-0.3 days',
      changeType: 'positive',
      threshold: 5,
      icon: 'Calendar',
      color: 'var(--color-primary)'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
      />
      <main className={`
        pt-16 medical-transition
        ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}
      `}>
        <div className="p-4 lg:p-6">
          {/* Breadcrumb Navigation */}
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">Patient Care Analytics</h1>
                <p className="text-text-secondary mt-1">
                  Monitor patient outcomes, demographics, and care quality metrics
                </p>
              </div>
              <div className="hidden md:flex items-center space-x-2 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-novus-green rounded-full pulse-indicator"></div>
                  <span>Real-time Data</span>
                </div>
                <span>•</span>
                <span>HIPAA Compliant</span>
                <span>•</span>
                <span>Last Updated: {new Date()?.toLocaleTimeString('en-US', { 
                  hour12: false, 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}</span>
              </div>
            </div>
          </div>

          {/* Filter Header */}
          <FilterHeader onFiltersChange={handleFiltersChange} />

          {/* Primary Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {patientMetrics?.map((metric, index) => (
              <PatientMetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                unit={metric?.unit}
                change={metric?.change}
                changeType={metric?.changeType}
                threshold={metric?.threshold}
                icon={metric?.icon}
                color={metric?.color}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mb-8">
            {/* Patient Journey Funnel - 8 columns */}
            <div className="xl:col-span-8">
              <PatientJourneyFunnel />
            </div>
            
            {/* Demographics Panel - 4 columns */}
            <div className="xl:col-span-4">
              <DemographicsPanel />
            </div>
          </div>

          {/* Full-width Outcome Heatmap */}
          <div className="mb-8">
            <OutcomeHeatmap />
          </div>

          {/* Footer Information */}
          <div className="bg-card border border-border rounded-lg p-6 medical-shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Data Sources</h4>
                <div className="space-y-1 text-sm text-text-secondary">
                  <div>• Electronic Health Records (EHR)</div>
                  <div>• Patient Management System</div>
                  <div>• Clinical Decision Support</div>
                  <div>• Patient Satisfaction Surveys</div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Compliance & Security</h4>
                <div className="space-y-1 text-sm text-text-secondary">
                  <div>• HIPAA Compliant Data Handling</div>
                  <div>• End-to-End Encryption</div>
                  <div>• Audit Trail Maintained</div>
                  <div>• Regular Security Assessments</div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Data Refresh</h4>
                <div className="space-y-1 text-sm text-text-secondary">
                  <div>• Real-time: Patient Status</div>
                  <div>• Every 15 min: Operational Metrics</div>
                  <div>• Hourly: Satisfaction Scores</div>
                  <div>• Daily: Outcome Analytics</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PatientCareAnalytics;